import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "./TimeSheets.css";
import {
  getApprovalQueueRowBadgeStatus,
  getApprovalQueueRowStatusLabel,
  getTimeSheetStatusLabel,
  TimeSheetApprovalStatus,
  TimeSheetStatus,
} from "./timesheetConstants";
import TimeSheetStatusBadge from "./TimeSheetStatusBadge";
import TimeSheetPersonCell from "./TimeSheetPersonCell";
import TimeSheetApprovalVelocitySection from "./TimeSheetApprovalVelocitySection";
import {
  buildApprovalQueueExportQuery,
  buildApprovalQueueQuery,
  fetchApprovalQueue,
  fetchApprovalQueueExportCsv,
  fetchApprovalQueueExportExcel,
  fetchApprovalQueueExportPdf,
  fetchApprovalQueueSummary,
  fetchApprovalTimesheetDetail,
  fetchApprovalTimesheetPdf,
  fetchTimesheetProjects,
  handleTimesheetApiError,
  mapApprovalQueueSummary,
  mapTimesheetProjectsResponse,
  postApprovalDecision,
  triggerBlobDownload,
} from "./timesheetApi";
import { subscribeTimesheetRealtime } from "./timesheetRealtimeBus";
import {
  addDays,
  addMonths,
  formatMonthRangeLabel,
  formatMonthYearLabel,
  formatWeekRangeReadable,
  getWeekStartKey,
  getWeekStartMonday,
  endOfMonth,
  getWeekStartsOverlappingMonth,
  startOfMonth,
} from "./timesheetDateUtils";

const PAGE_SIZE = 20;

/** Client-side sort keys for the approval queue grid (current page). */
const APPROVAL_SORT = {
  employee: "employee",
  department: "department",
  office: "office",
  projects: "projects",
  period: "period",
  hours: "hours",
  status: "status",
};

const VIEW = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
};

const EMPTY_APPROVAL_SUMMARY = {
  total: 0,
  awaitingApproval: 0,
  approved: 0,
  returned: 0,
  rejected: 0,
};

const ACTION = {
  APPROVE: "approve",
  REJECT: "reject",
  RETURN: "return",
};

const rowShowsAction = (row, action) => {
  if (row.availableActions?.length) return row.availableActions.includes(action);
  return (row.approvalStatus ?? TimeSheetApprovalStatus.Pending) === TimeSheetApprovalStatus.Pending;
};

/** Stable group key for queue rows (normalized email, else per-user fallback). */
const approvalQueueEmailGroupKey = (row) => {
  const e = String(row.email ?? "").trim().toLowerCase();
  if (e) return e;
  const uid = String(row.employeeUserId ?? "").trim();
  if (uid) return `uid:${uid}`;
  return `row:${row.id}`;
};

const TimeSheetApprovalsPage = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(VIEW.MONTHLY);
  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const [focusWeekStart, setFocusWeekStart] = useState(null);
  const [filters, setFilters] = useState({ project: "all", status: "all" });
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [officeFilter, setOfficeFilter] = useState("all");
  const [modal, setModal] = useState(null); // { action, row }
  const [detailRow, setDetailRow] = useState(null);
  const [detailDto, setDetailDto] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPdfLoading, setDetailPdfLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [queuePaged, setQueuePaged] = useState(null);
  const [page, setPage] = useState(1);
  const [queueRefreshKey, setQueueRefreshKey] = useState(0);
  const [exportLoading, setExportLoading] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [queueSummary, setQueueSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [sortState, setSortState] = useState({ key: APPROVAL_SORT.employee, dir: "asc" });
  /** Email-group keys with `true` = collapsed (multi-row groups only). */
  const [apprGroupCollapsed, setApprGroupCollapsed] = useState(() => ({}));

  const detailRowRef = useRef(null);
  detailRowRef.current = detailRow;

  useEffect(() => {
    return subscribeTimesheetRealtime((data) => {
      setQueueRefreshKey((k) => k + 1);
      const row = detailRowRef.current;
      if (row?.timeSheetId != null && String(row.timeSheetId) === String(data.timeSheetId)) {
        setDetailLoading(true);
        fetchApprovalTimesheetDetail(row.timeSheetId)
          .then((dto) => setDetailDto(dto))
          .catch((e) => {
            setDetailDto(null);
            handleTimesheetApiError(e, t("app.timesheets.approvals.detailLoadFailed"));
          })
          .finally(() => setDetailLoading(false));
      }
    });
  }, [t]);

  const weekStartsInMonth = useMemo(() => getWeekStartsOverlappingMonth(monthStart), [monthStart]);

  useEffect(() => {
    if (!weekStartsInMonth.length) {
      setFocusWeekStart(null);
      return;
    }
    setFocusWeekStart((prev) => {
      if (!prev) return weekStartsInMonth[0];
      const ok = weekStartsInMonth.some((w) => w.getTime() === prev.getTime());
      return ok ? prev : weekStartsInMonth[0];
    });
  }, [monthStart, weekStartsInMonth]);

  const effectiveWeekStart = focusWeekStart || getWeekStartMonday(monthStart);
  const weekEnd = useMemo(() => addDays(effectiveWeekStart, 6), [effectiveWeekStart]);
  const rangeLabel = formatWeekRangeReadable(effectiveWeekStart, weekEnd);
  const selectedWeekKey = useMemo(() => getWeekStartKey(effectiveWeekStart), [effectiveWeekStart]);

  const velocityPeriod = useMemo(() => {
    if (viewMode === VIEW.WEEKLY) {
      const periodStart = new Date(effectiveWeekStart);
      periodStart.setHours(0, 0, 0, 0);
      const periodEnd = new Date(weekEnd);
      periodEnd.setHours(23, 59, 59, 999);
      return { periodStart, periodEnd };
    }
    const y = monthStart.getFullYear();
    const m = monthStart.getMonth();
    const periodStart = new Date(y, m, 1, 0, 0, 0, 0);
    const periodEnd = new Date(y, m + 1, 0, 23, 59, 59, 999);
    return { periodStart, periodEnd };
  }, [viewMode, effectiveWeekStart, weekEnd, monthStart]);

  const periodPillLabel = viewMode === VIEW.WEEKLY ? formatMonthYearLabel(monthStart) : formatMonthRangeLabel(monthStart);

  const showJumpToCurrentPeriod = useMemo(() => {
    const now = new Date();
    const inCurrentMonth =
      monthStart.getFullYear() === now.getFullYear() && monthStart.getMonth() === now.getMonth();
    if (viewMode === VIEW.MONTHLY) return !inCurrentMonth;
    const tw = getWeekStartMonday(now);
    const onCurrentWeek = focusWeekStart != null && focusWeekStart.getTime() === tw.getTime();
    return !inCurrentMonth || !onCurrentWeek;
  }, [monthStart, viewMode, focusWeekStart]);

  const jumpToCurrentPeriod = () => {
    const now = new Date();
    setMonthStart(startOfMonth(now));
    if (viewMode === VIEW.WEEKLY) {
      setFocusWeekStart(getWeekStartMonday(now));
    }
  };

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedEmployeeSearch(employeeQuery.trim()), 350);
    return () => window.clearTimeout(id);
  }, [employeeQuery]);

  useEffect(() => {
    setPage(1);
  }, [viewMode, monthStart, focusWeekStart, filters.project, filters.status, departmentFilter, officeFilter, debouncedEmployeeSearch]);

  const queueQueryArgs = useMemo(
    () => ({
      approvalsViewMode: viewMode === VIEW.MONTHLY ? "monthly" : "weekly",
      monthStart,
      effectiveWeekStart,
      weekEnd,
      filters,
      departmentFilter,
      officeFilter,
      employeeSearch: debouncedEmployeeSearch,
    }),
    [viewMode, monthStart, effectiveWeekStart, weekEnd, filters, departmentFilter, officeFilter, debouncedEmployeeSearch]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListLoading(true);
      try {
        const params = buildApprovalQueueQuery({ ...queueQueryArgs, page, take: PAGE_SIZE });
        const data = await fetchApprovalQueue(params);
        if (!cancelled) setQueuePaged(data);
      } catch (e) {
        if (!cancelled) {
          setQueuePaged(null);
          handleTimesheetApiError(e, t("app.timesheets.approvals.queueLoadFailed"));
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queueQueryArgs, page, queueRefreshKey, t]);

  useEffect(() => {
    setApprGroupCollapsed({});
  }, [page, queueRefreshKey]);

  /** Monthly stats: one Summary call for the full calendar month (avoids N calls per overlapping week). */
  useEffect(() => {
    if (viewMode !== VIEW.MONTHLY) return undefined;
    let cancelled = false;
    const load = async () => {
      setSummaryLoading(true);
      try {
        const from = startOfMonth(monthStart);
        const to = endOfMonth(monthStart);
        const raw = await fetchApprovalQueueSummary(from, to);
        if (cancelled) return;
        setQueueSummary(mapApprovalQueueSummary(raw) ?? EMPTY_APPROVAL_SUMMARY);
      } catch (e) {
        if (!cancelled) {
          setQueueSummary(EMPTY_APPROVAL_SUMMARY);
          handleTimesheetApiError(e, t("app.timesheets.approvals.summaryLoadFailed"));
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [viewMode, monthStart, queueRefreshKey, t]);

  /** Weekly stats: one Summary call for the selected week. */
  useEffect(() => {
    if (viewMode !== VIEW.WEEKLY) return undefined;
    let cancelled = false;
    const load = async () => {
      setSummaryLoading(true);
      try {
        const raw = await fetchApprovalQueueSummary(effectiveWeekStart, weekEnd);
        if (cancelled) return;
        setQueueSummary(mapApprovalQueueSummary(raw) ?? EMPTY_APPROVAL_SUMMARY);
      } catch (e) {
        if (!cancelled) {
          setQueueSummary(EMPTY_APPROVAL_SUMMARY);
          handleTimesheetApiError(e, t("app.timesheets.approvals.summaryLoadFailed"));
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [viewMode, effectiveWeekStart, weekEnd, queueRefreshKey, t]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const { Department, Office } = config.REST_API;
        const [depRes, offRes] = await Promise.all([
          axiosBasic.get(Department.Get),
          axiosBasic.get(Office.GetCompanyOffices),
        ]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        } else {
          setDepartments([]);
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({
                id: o.id,
                name: o.countryName ? `${o.name} (${o.countryName})` : o.name,
              }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        } else {
          setOffices([]);
        }
      } catch {
        setDepartments([]);
        setOffices([]);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loadOnce = async () => {
        const raw = await fetchTimesheetProjects();
        if (cancelled) return [];
        return mapTimesheetProjectsResponse(raw);
      };
      try {
        let list = await loadOnce();
        if (!cancelled && list.length === 0) {
          await new Promise((r) => setTimeout(r, 400));
          if (!cancelled) list = await loadOnce();
        }
        if (!cancelled) setProjectOptions(list);
      } catch {
        if (!cancelled) setProjectOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** From `UserTimeSheetApproval/Summary`: weekly = one call; monthly = merged per week in month. */
  const stats = queueSummary ?? EMPTY_APPROVAL_SUMMARY;

  const queueItems = queuePaged?.items ?? [];
  const totalCount = queuePaged?.totalCount ?? 0;
  const totalPages = Math.max(1, queuePaged?.totalPages ?? 1);
  const hasNextPage = Boolean(queuePaged?.hasNextPage);
  const hasPreviousPage = Boolean(queuePaged?.hasPreviousPage);
  const showQueuePagination =
    !listLoading &&
    Boolean(queuePaged) &&
    (totalPages > 1 || page > 1 || hasNextPage || hasPreviousPage);

  const sortedQueueItems = useMemo(() => {
    const list = [...queueItems];
    const { key, dir } = sortState;
    const mul = dir === "asc" ? 1 : -1;
    const cmpStr = (a, b) => String(a ?? "").localeCompare(String(b ?? ""), undefined, { sensitivity: "base" });
    list.sort((a, b) => {
      switch (key) {
        case APPROVAL_SORT.employee:
          return mul * cmpStr(a.employeeName, b.employeeName);
        case APPROVAL_SORT.department:
          return mul * cmpStr(a.department, b.department);
        case APPROVAL_SORT.office:
          return mul * cmpStr(a.office, b.office);
        case APPROVAL_SORT.projects:
          return mul * cmpStr(a.projectsSummary, b.projectsSummary);
        case APPROVAL_SORT.period:
          return mul * cmpStr(a.periodLabel, b.periodLabel);
        case APPROVAL_SORT.hours:
          return mul * (Number(a.totalHours) - Number(b.totalHours));
        case APPROVAL_SORT.status:
          return mul * ((a.approvalStatus ?? 0) - (b.approvalStatus ?? 0));
        default:
          return 0;
      }
    });
    return list;
  }, [queueItems, sortState]);

  /**
   * One group per distinct email (or per-user fallback). Group order follows the first occurrence
   * of that address in the current sort; rows inside a group stay in sorted order.
   */
  const approvalQueueGroups = useMemo(() => {
    const byKey = new Map();
    const firstIndex = new Map();
    sortedQueueItems.forEach((row, i) => {
      const key = approvalQueueEmailGroupKey(row);
      if (!byKey.has(key)) {
        byKey.set(key, []);
        firstIndex.set(key, i);
      }
      byKey.get(key).push(row);
    });
    const keys = [...byKey.keys()].sort((a, b) => firstIndex.get(a) - firstIndex.get(b));
    return keys.map((key) => ({ key, rows: byKey.get(key) }));
  }, [sortedQueueItems]);

  const toggleApprovalSort = (columnKey) => {
    setSortState((prev) =>
      prev.key === columnKey
        ? { key: columnKey, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: columnKey, dir: "asc" }
    );
  };

  const toggleApprGroupCollapsed = useCallback((groupKey) => {
    setApprGroupCollapsed((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  const approvalSortAria = (columnKey, columnLabel) => {
    if (sortState.key !== columnKey) {
      return t("app.timesheets.approvals.sortActivate", { column: columnLabel });
    }
    return sortState.dir === "asc"
      ? t("app.timesheets.approvals.sortToggleDesc", { column: columnLabel })
      : t("app.timesheets.approvals.sortToggleAsc", { column: columnLabel });
  };

  const clearFilters = () => {
    setFilters({ project: "all", status: "all" });
    setEmployeeQuery("");
    setDepartmentFilter("all");
    setOfficeFilter("all");
  };

  const shiftMonth = (delta) => setMonthStart((m) => startOfMonth(addMonths(m, delta)));

  const shiftFocusWeek = (delta) => {
    const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
    if (i < 0) return;
    const next = weekStartsInMonth[i + delta];
    if (next) setFocusWeekStart(next);
  };

  const scopeLabel = viewMode === VIEW.MONTHLY ? formatMonthRangeLabel(monthStart) : rangeLabel;

  const openDetail = useCallback(
    (row) => {
      setDetailRow(row);
      setDetailDto(null);
      if (!row?.timeSheetId) {
        Common.showErrorMessage(t("app.timesheets.approvals.missingTimeSheetId"));
        return;
      }
      setDetailLoading(true);
      fetchApprovalTimesheetDetail(row.timeSheetId)
        .then((dto) => setDetailDto(dto))
        .catch((e) => {
          setDetailDto(null);
          handleTimesheetApiError(e, t("app.timesheets.approvals.detailLoadFailed"));
        })
        .finally(() => setDetailLoading(false));
    },
    [t]
  );

  const closeDetail = useCallback(() => {
    setDetailRow(null);
    setDetailDto(null);
  }, []);

  const approvalQueueExportBasename = () =>
    viewMode === VIEW.MONTHLY
      ? `timesheet-approvals-${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}`
      : `timesheet-approvals-week-${selectedWeekKey}`;

  const runExportExcel = async () => {
    setExportLoading("excel");
    try {
      const query = buildApprovalQueueExportQuery(queueQueryArgs);
      const blob = await fetchApprovalQueueExportExcel(query);
      triggerBlobDownload(blob, `${approvalQueueExportBasename()}.xlsx`);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.approvals.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportCsv = async () => {
    setExportLoading("csv");
    try {
      const query = buildApprovalQueueExportQuery(queueQueryArgs);
      const blob = await fetchApprovalQueueExportCsv(query);
      triggerBlobDownload(blob, `${approvalQueueExportBasename()}.csv`);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.approvals.exportCsvFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportQueuePdf = async () => {
    setExportLoading("pdf");
    try {
      const query = buildApprovalQueueExportQuery(queueQueryArgs);
      const blob = await fetchApprovalQueueExportPdf(query);
      triggerBlobDownload(blob, `${approvalQueueExportBasename()}.pdf`);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.approvals.exportQueuePdfFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const downloadDetailPdf = async () => {
    if (!detailRow?.timeSheetId) return;
    setDetailPdfLoading(true);
    try {
      const blob = await fetchApprovalTimesheetPdf(detailRow.timeSheetId);
      triggerBlobDownload(blob, `timesheet-${detailRow.timeSheetId}.pdf`);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.approvals.pdfDownloadFailed"));
    } finally {
      setDetailPdfLoading(false);
    }
  };

  const applyAction = async (reason) => {
    if (!modal) return;
    const { action, row } = modal;
    if (!row.approvalId) {
      Common.showErrorMessage(t("app.timesheets.approvals.missingApprovalId"));
      return;
    }
    setDecisionLoading(true);
    try {
      await postApprovalDecision({
        approvalId: row.approvalId,
        action,
        remarks: reason || "",
      });
      if (action === ACTION.APPROVE) {
        Common.showSuccessMessage(t("app.timesheets.approvals.approvedSuccess", { name: row.employeeName }));
      } else if (action === ACTION.REJECT) {
        Common.showSuccessMessage(t("app.timesheets.approvals.rejectedSuccess", { name: row.employeeName }));
      } else if (action === ACTION.RETURN) {
        Common.showSuccessMessage(t("app.timesheets.approvals.returnedSuccess", { name: row.employeeName }));
      }
      setModal(null);
      setQueueRefreshKey((k) => k + 1);
      if (detailRow && detailRow.approvalId === row.approvalId) closeDetail();
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.approvals.decisionFailed"));
    } finally {
      setDecisionLoading(false);
    }
  };

  const gridCols =
    "minmax(168px, 1fr) minmax(118px, 0.58fr) minmax(118px, 0.58fr) minmax(96px, 0.88fr) 1.02fr 0.48fr 0.78fr minmax(176px, 0.82fr)";

  const renderApprovalQueueRow = (row, showPersonCell) => {
    const showApprove = rowShowsAction(row, ACTION.APPROVE);
    const showReject = rowShowsAction(row, ACTION.REJECT);
    const showReturn = rowShowsAction(row, ACTION.RETURN);
    const canOpenDetail = Boolean(String(row.timeSheetId ?? "").trim());
    const decisionTip = t("app.timesheets.approvals.decisionUnavailableTooltip");
    return (
      <div
        key={row.id}
        className={`ts-table-row ts-table-row--approvals${showPersonCell ? "" : " ts-table-row--appr-grouped"}`}
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className={showPersonCell ? undefined : "ts-appr-group-employee-slot"} style={{ minWidth: 0 }}>
          {showPersonCell ? (
            <TimeSheetPersonCell
              variant="approval"
              name={row.employeeName}
              designation={row.designation}
              email={row.email}
              photoUrl={row.photoUrl}
            />
          ) : (
            <div
              className="ts-appr-group-continuation-marker"
              aria-hidden="true"
              title={t("app.timesheets.approvals.queueGroupContinuationHint")}
            />
          )}
        </div>
        <div
          className="ts-appr-queue-meta-cell small text-secondary"
          style={{ minWidth: 0 }}
          title={row.department || undefined}
        >
          {row.department || "—"}
        </div>
        <div
          className="ts-appr-queue-meta-cell small text-secondary"
          style={{ minWidth: 0 }}
          title={row.office || undefined}
        >
          {row.office || "—"}
        </div>
        <div className="ts-appr-projects-cell" style={{ minWidth: 0 }}>
          {row.projectLabels?.length ? (
            <div
              className="ts-appr-project-tags"
              role="list"
              aria-label={t("app.timesheets.approvals.colProjects")}
            >
              {row.projectLabels.map((label, i) => (
                <span key={`${row.id}-proj-${i}`} className="ts-appr-project-tag" role="listitem">
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <span className="ts-appr-project-tags ts-appr-project-tags--empty text-muted">—</span>
          )}
        </div>
        <div className="ts-appr-period-cell">{row.periodLabel}</div>
        <div style={{ textAlign: "center", fontWeight: 800 }}>{Number(row.totalHours).toFixed(1)}h</div>
        <div style={{ textAlign: "center" }}>
          <TimeSheetStatusBadge
            status={getApprovalQueueRowBadgeStatus(row)}
            label={getApprovalQueueRowStatusLabel(row, t)}
          />
        </div>
        <div className="documents-actions-inline ts-appr-actions-inline ts-appr-actions-stack">
          <button
            type="button"
            className="documents-grid-action-btn ts-appr-btn-view-detail ts-appr-btn-view-detail--compact"
            title={t("app.timesheets.approvals.detailTooltip")}
            aria-label={t("app.timesheets.approvals.detailTooltip")}
            disabled={!canOpenDetail}
            onClick={() => openDetail(row)}
          >
            {t("app.timesheets.approvals.viewDetail")}
          </button>
          <div
            className="ts-appr-decision-group"
            role="group"
            aria-label={t("app.timesheets.approvals.decisionsGroupAria")}
          >
            <button
              type="button"
              className="documents-grid-action-btn ts-tl-btn-submit-for-approval ts-appr-decision-btn"
              disabled={!showApprove}
              title={!showApprove ? decisionTip : undefined}
              onClick={() => setModal({ action: ACTION.APPROVE, row })}
            >
              {t("app.timesheets.approvals.approve")}
            </button>
            <button
              type="button"
              className="documents-grid-action-btn documents-grid-action-btn--delete ts-appr-decision-btn"
              disabled={!showReject}
              title={!showReject ? decisionTip : undefined}
              onClick={() => setModal({ action: ACTION.REJECT, row })}
            >
              {t("app.timesheets.approvals.reject")}
            </button>
            <button
              type="button"
              className="documents-grid-action-btn ts-tl-btn-revert ts-appr-decision-btn"
              disabled={!showReturn}
              title={!showReturn ? decisionTip : undefined}
              onClick={() => setModal({ action: ACTION.RETURN, row })}
            >
              {t("app.timesheets.approvals.return")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const statValue = (n) => (summaryLoading ? "…" : n);

  return (
    <div className="documents-page ts-page ts-page--approvals container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-check2-square" />}
        title={t("app.timesheets.approvals.title")}
        description={t("app.timesheets.approvals.description")}
      />

      <section
        className="ts-appr-stats"
        aria-label={t("app.timesheets.approvals.statsAria")}
        aria-busy={summaryLoading}
      >
        <div className="ts-appr-stat-card ts-appr-stat-card--total">
          <div className="ts-appr-stat-card__accent" aria-hidden />
          <div className="ts-appr-stat-card__inner">
            <div className="ts-appr-stat-card__top">
              <span className="ts-appr-stat-card__icon">
                <i className="bi bi-briefcase" />
              </span>
              <div className="ts-appr-stat-card__figures">
                <span className="ts-appr-stat-card__value">{statValue(stats.total)}</span>
                <span className="ts-appr-stat-card__label">{t("app.timesheets.approvals.statTotal")}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
          <div className="ts-appr-stat-card__accent" aria-hidden />
          <div className="ts-appr-stat-card__inner">
            <div className="ts-appr-stat-card__top">
              <span className="ts-appr-stat-card__icon">
                <i className="bi bi-hourglass-split" />
              </span>
              <div className="ts-appr-stat-card__figures">
                <span className="ts-appr-stat-card__value">{statValue(stats.awaitingApproval)}</span>
                <span className="ts-appr-stat-card__label">{t("app.timesheets.approvals.statAwaiting")}</span>
              </div>
            </div>
            <div className="ts-appr-stat-card__pill-wrap">
              <TimeSheetStatusBadge
                status={TimeSheetStatus.Submitted}
                label={t("app.timesheets.approvals.statusPending")}
              />
            </div>
          </div>
        </div>
        <div className="ts-appr-stat-card ts-appr-stat-card--approved">
          <div className="ts-appr-stat-card__accent" aria-hidden />
          <div className="ts-appr-stat-card__inner">
            <div className="ts-appr-stat-card__top">
              <span className="ts-appr-stat-card__icon">
                <i className="bi bi-check-circle-fill" />
              </span>
              <div className="ts-appr-stat-card__figures">
                <span className="ts-appr-stat-card__value">{statValue(stats.approved)}</span>
                <span className="ts-appr-stat-card__label">{t("app.timesheets.approvals.statApproved")}</span>
              </div>
            </div>
            <div className="ts-appr-stat-card__pill-wrap">
              <TimeSheetStatusBadge
                status={TimeSheetStatus.Approved}
                label={getTimeSheetStatusLabel(TimeSheetStatus.Approved)}
              />
            </div>
          </div>
        </div>
        <div className="ts-appr-stat-card ts-appr-stat-card--returned">
          <div className="ts-appr-stat-card__accent" aria-hidden />
          <div className="ts-appr-stat-card__inner">
            <div className="ts-appr-stat-card__top">
              <span className="ts-appr-stat-card__icon">
                <i className="bi bi-arrow-return-left" />
              </span>
              <div className="ts-appr-stat-card__figures">
                <span className="ts-appr-stat-card__value">{statValue(stats.returned)}</span>
                <span className="ts-appr-stat-card__label">{t("app.timesheets.approvals.statReturned")}</span>
              </div>
            </div>
            <div className="ts-appr-stat-card__pill-wrap">
              <TimeSheetStatusBadge
                status={TimeSheetStatus.Returned}
                label={getTimeSheetStatusLabel(TimeSheetStatus.Returned)}
              />
            </div>
          </div>
        </div>
        <div className="ts-appr-stat-card ts-appr-stat-card--rejected">
          <div className="ts-appr-stat-card__accent" aria-hidden />
          <div className="ts-appr-stat-card__inner">
            <div className="ts-appr-stat-card__top">
              <span className="ts-appr-stat-card__icon">
                <i className="bi bi-x-circle-fill" />
              </span>
              <div className="ts-appr-stat-card__figures">
                <span className="ts-appr-stat-card__value">{statValue(stats.rejected)}</span>
                <span className="ts-appr-stat-card__label">{t("app.timesheets.approvals.statRejected")}</span>
              </div>
            </div>
            <div className="ts-appr-stat-card__pill-wrap">
              <TimeSheetStatusBadge
                status={TimeSheetStatus.Rejected}
                label={getTimeSheetStatusLabel(TimeSheetStatus.Rejected)}
              />
            </div>
          </div>
        </div>
      </section>

      <TimeSheetApprovalVelocitySection
        periodStart={velocityPeriod.periodStart}
        periodEnd={velocityPeriod.periodEnd}
        refreshKey={queueRefreshKey}
      />

      <section className="ts-time-log-shell" aria-label={t("app.timesheets.approvals.filtersAria")}>
        <div className="ts-appr-filters" role="search">
          <div className="ts-appr-filters__title text-muted" id="ts-appr-filters-heading">
            {t("app.timesheets.approvals.filtersToolbarLabel")}
          </div>

          <div className="ts-appr-filters__toolbar" aria-labelledby="ts-appr-filters-heading">
            <div className="ts-appr-filters__toolbar-left">
              <div
                className="ts-view-tabs ts-view-tabs--two ts-view-tabs--timelog-inline"
                role="tablist"
                aria-label={t("app.timesheets.timeLog.viewMode")}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === VIEW.MONTHLY}
                  className={viewMode === VIEW.MONTHLY ? "is-active" : ""}
                  onClick={() => setViewMode(VIEW.MONTHLY)}
                >
                  {t("app.timesheets.timeLog.viewMonthly")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === VIEW.WEEKLY}
                  className={viewMode === VIEW.WEEKLY ? "is-active" : ""}
                  onClick={() => setViewMode(VIEW.WEEKLY)}
                >
                  {t("app.timesheets.timeLog.viewWeekly")}
                </button>
              </div>
              <span className="ts-appr-filters__toolbar-divider d-none d-sm-block" aria-hidden />
              <div className="ts-tl-period-toolbar-group ts-appr-filters__period-wrap">
                <div
                  className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact"
                  aria-label={t("app.timesheets.timeLog.periodRangeAria")}
                >
                  <button type="button" onClick={() => shiftMonth(-1)} aria-label={t("app.timesheets.common.prevPeriod")}>
                    <i className="bi bi-chevron-left" />
                  </button>
                  <span
                    className="ts-period-pill ts-period-pill--inline ts-period-pill--compact"
                    title={viewMode === VIEW.WEEKLY ? formatMonthRangeLabel(monthStart) : undefined}
                  >
                    <i className="bi bi-calendar3 text-primary" aria-hidden />
                    {periodPillLabel}
                  </span>
                  <button type="button" onClick={() => shiftMonth(1)} aria-label={t("app.timesheets.common.nextPeriod")}>
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
                {showJumpToCurrentPeriod ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm ts-tl-jump-current"
                    onClick={jumpToCurrentPeriod}
                    aria-label={
                      viewMode === VIEW.MONTHLY
                        ? t("app.timesheets.timeLog.jumpToCurrentMonthAria")
                        : t("app.timesheets.timeLog.jumpToCurrentWeekToolbarAria")
                    }
                  >
                    <i className="bi bi-calendar-check me-1" aria-hidden />
                    {viewMode === VIEW.MONTHLY
                      ? t("app.timesheets.timeLog.jumpToCurrentMonth")
                      : t("app.timesheets.timeLog.jumpToCurrentWeekToolbar")}
                  </button>
                ) : null}
              </div>
              <span className="ts-appr-filters__toolbar-divider d-none d-md-block" aria-hidden />
              <div
                className="ts-time-log-export-group ts-time-log-export-group--labeled ts-appr-filters__exports"
                role="group"
                aria-label={t("app.timesheets.approvals.exportActionsAria")}
              >
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                  onClick={() => runExportExcel()}
                  disabled={Boolean(exportLoading) || listLoading}
                  title={t("app.timesheets.approvals.exportExcelTitle")}
                >
                  {exportLoading === "excel" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                  onClick={() => runExportCsv()}
                  disabled={Boolean(exportLoading) || listLoading}
                  title={t("app.timesheets.approvals.exportCsvTitle")}
                >
                  {exportLoading === "csv" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-filetype-csv" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                  onClick={() => runExportQueuePdf()}
                  disabled={Boolean(exportLoading) || listLoading}
                  title={t("app.timesheets.approvals.exportQueuePdfTitle")}
                >
                  {exportLoading === "pdf" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-pdf" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
                </button>
              </div>
              {viewMode === VIEW.WEEKLY && weekStartsInMonth.length > 0 ? (
                <>
                  <span className="ts-appr-filters__toolbar-divider d-none d-lg-block" aria-hidden />
                  <div
                    className="ts-time-log-week-nav ts-time-log-week-nav--filter ts-time-log-week-nav--toolbar-inline ts-appr-filters__week-nav"
                    role="group"
                    aria-label={t("app.timesheets.timeLog.weekNavAria")}
                  >
                    <button
                      type="button"
                      className="ts-time-log-week-nav-btn ts-time-log-week-nav-btn--toolbar"
                      onClick={() => shiftFocusWeek(-1)}
                      disabled={(() => {
                        const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
                        return i <= 0;
                      })()}
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <span className="ts-time-log-week-label" id="ts-appr-week-range">
                      {rangeLabel}
                    </span>
                    <button
                      type="button"
                      className="ts-time-log-week-nav-btn ts-time-log-week-nav-btn--toolbar"
                      onClick={() => shiftFocusWeek(1)}
                      disabled={(() => {
                        const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
                        return i < 0 || i >= weekStartsInMonth.length - 1;
                      })()}
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                </>
              ) : null}
            </div>
            <p className="ts-appr-filters__toolbar-hint ts-appr-controls__hint">
              {viewMode === VIEW.MONTHLY
                ? t("app.timesheets.approvals.monthScopeHint")
                : t("app.timesheets.approvals.weekScopeHint")}
            </p>
          </div>

          <div className="ts-appr-filters__fields">
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-search">
                {t("app.timesheets.approvals.searchEmployee")}
              </label>
              <input
                id="ts-appr-search"
                type="search"
                autoComplete="off"
                placeholder={t("app.timesheets.approvals.searchPlaceholder")}
                value={employeeQuery}
                onChange={(e) => setEmployeeQuery(e.target.value)}
              />
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-dept">
                {t("app.timesheets.approvals.filterDepartment")}
              </label>
              <select
                id="ts-appr-dept"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="all">{t("app.timesheets.approvals.filterAllDepartments")}</option>
                {departments.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-office">
                {t("app.timesheets.approvals.filterOffice")}
              </label>
              <select
                id="ts-appr-office"
                value={officeFilter}
                onChange={(e) => setOfficeFilter(e.target.value)}
              >
                <option value="all">{t("app.timesheets.approvals.filterAllOffices")}</option>
                {offices.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-project">
                {t("app.timesheets.timeLog.filterProject")}
              </label>
              <select
                id="ts-appr-project"
                value={filters.project}
                onChange={(e) => setFilters((f) => ({ ...f, project: e.target.value }))}
              >
                <option value="all">{t("app.timesheets.timeLog.allProjects")}</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.teamName ? `${p.name} — ${p.teamName}` : p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field ts-appr-filters__filter--status">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-status">
                {t("app.timesheets.approvals.filterStatusLabel")}
              </label>
              <select
                id="ts-appr-status"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="all">{t("app.timesheets.approvals.filterAnyStatus")}</option>
                <option value={String(TimeSheetApprovalStatus.Pending)}>{t("app.timesheets.approvals.statusPending")}</option>
                <option value={String(TimeSheetApprovalStatus.Approved)}>
                  {getTimeSheetStatusLabel(TimeSheetStatus.Approved)}
                </option>
                <option value={String(TimeSheetApprovalStatus.Returned)}>
                  {getTimeSheetStatusLabel(TimeSheetStatus.Returned)}
                </option>
                <option value={String(TimeSheetApprovalStatus.Rejected)}>
                  {getTimeSheetStatusLabel(TimeSheetStatus.Rejected)}
                </option>
              </select>
            </div>
            <div className="ts-filter-field ts-appr-filters__clear-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-appr-clear">
                {t("app.timesheets.approvals.clearFiltersLabel")}
              </label>
              <button
                id="ts-appr-clear"
                type="button"
                className="btn btn-outline-secondary ts-tl-clear-filters-btn ts-appr-filters__clear-btn"
                onClick={clearFilters}
              >
                <i className="bi bi-x-lg me-1" aria-hidden />
                {t("app.timesheets.approvals.clearFilters")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <p className="documents-page-description ts-appr-results-hint mb-3">
        {t("app.timesheets.approvals.resultsPagedHint", {
          shown: queueItems.length,
          total: totalCount,
          period: scopeLabel,
        })}
      </p>

      <div className="ts-table-wrap ts-table-wrap--approvals">
        <div className="ts-table-head" style={{ gridTemplateColumns: gridCols }}>
          <div className="ts-appr-th">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.employee ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.employee)}
              aria-label={approvalSortAria(APPROVAL_SORT.employee, t("app.timesheets.approvals.colEmployee"))}
            >
              <span>{t("app.timesheets.approvals.colEmployee")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.employee
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.department ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.department)}
              aria-label={approvalSortAria(APPROVAL_SORT.department, t("app.timesheets.approvals.colDepartment"))}
            >
              <span>{t("app.timesheets.approvals.colDepartment")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.department
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.office ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.office)}
              aria-label={approvalSortAria(APPROVAL_SORT.office, t("app.timesheets.approvals.colOffice"))}
            >
              <span>{t("app.timesheets.approvals.colOffice")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.office
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.projects ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.projects)}
              aria-label={approvalSortAria(APPROVAL_SORT.projects, t("app.timesheets.approvals.colProjects"))}
            >
              <span>{t("app.timesheets.approvals.colProjects")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.projects
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.period ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.period)}
              aria-label={approvalSortAria(APPROVAL_SORT.period, t("app.timesheets.approvals.colPeriod"))}
            >
              <span>{t("app.timesheets.approvals.colPeriod")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.period
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th ts-appr-th--center">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.hours ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.hours)}
              aria-label={approvalSortAria(APPROVAL_SORT.hours, t("app.timesheets.approvals.colHours"))}
            >
              <span>{t("app.timesheets.approvals.colHours")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.hours
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-th ts-appr-th--center">
            <button
              type="button"
              className={`ts-appr-sort-head ${sortState.key === APPROVAL_SORT.status ? "ts-appr-sort-head--active" : ""}`}
              onClick={() => toggleApprovalSort(APPROVAL_SORT.status)}
              aria-label={approvalSortAria(APPROVAL_SORT.status, t("app.timesheets.approvals.colStatus"))}
            >
              <span>{t("app.timesheets.approvals.colStatus")}</span>
              <i
                className={`bi ${
                  sortState.key === APPROVAL_SORT.status
                    ? sortState.dir === "asc"
                      ? "bi-sort-up-alt"
                      : "bi-sort-down-alt"
                    : "bi-arrow-down-up"
                }`}
                aria-hidden
              />
            </button>
          </div>
          <div className="ts-appr-actions-head">{t("app.timesheets.approvals.colActions")}</div>
        </div>
        {listLoading ? (
          <div className="d-flex justify-content-center align-items-center gap-2 py-5 px-3 text-muted border-top border-bottom ts-appr-list-loading" role="status" aria-live="polite">
            <span className="spinner-border spinner-border-sm text-primary" aria-hidden />
            {t("app.timesheets.approvals.loading")}
          </div>
        ) : (
          <>
            {approvalQueueGroups.map((group, groupIndex) => {
              const rep = group.rows[0];
              const multi = group.rows.length > 1;
              const groupAria = t("app.timesheets.approvals.queueGroupAria", {
                name: rep.employeeName,
                email: String(rep.email ?? "").trim() || t("app.timesheets.approvals.queueGroupNoEmail"),
                count: group.rows.length,
              });
              if (!multi) {
                return renderApprovalQueueRow(rep, true);
              }
              const collapsed = Boolean(apprGroupCollapsed[group.key]);
              const groupBodyId = `ts-appr-queue-grp-body-${groupIndex}`;
              const toggleLabel = collapsed
                ? t("app.timesheets.approvals.queueGroupExpandAria", {
                    name: rep.employeeName,
                    count: group.rows.length,
                  })
                : t("app.timesheets.approvals.queueGroupCollapseAria", { name: rep.employeeName });
              return (
                <div key={group.key} className="ts-appr-queue-group" role="group" aria-label={groupAria}>
                  <div className="ts-appr-group-head">
                    <button
                      type="button"
                      className="ts-appr-group-toggle"
                      aria-expanded={!collapsed}
                      aria-controls={groupBodyId}
                      aria-label={toggleLabel}
                      onClick={() => toggleApprGroupCollapsed(group.key)}
                    >
                      <i className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-down"}`} aria-hidden />
                    </button>
                    <TimeSheetPersonCell
                      variant="approval"
                      name={rep.employeeName}
                      designation={rep.designation}
                      email={rep.email}
                      photoUrl={rep.photoUrl}
                    />
                    <span className="ts-appr-group-count">
                      {t("app.timesheets.approvals.queueGroupCount", { count: group.rows.length })}
                    </span>
                  </div>
                  <div
                    id={groupBodyId}
                    className="ts-appr-group-body"
                    hidden={collapsed}
                    aria-hidden={collapsed}
                  >
                    {group.rows.map((row) => renderApprovalQueueRow(row, false))}
                  </div>
                </div>
              );
            })}
            {queueItems.length === 0 ? (
              <div className="ts-appr-empty">{t("app.timesheets.approvals.empty")}</div>
            ) : null}
          </>
        )}
      </div>

      {showQueuePagination ? (
        <div className="d-flex justify-content-center align-items-center gap-2 py-3 ts-appr-pagination">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t("app.timesheets.timeLog.paginationPrev")}
          </button>
          <span className="small text-muted">
            {t("app.timesheets.approvals.paginationPage", { page, totalPages, total: totalCount })}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={!hasNextPage && page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("app.timesheets.timeLog.paginationNext")}
          </button>
        </div>
      ) : null}

      {detailRow &&
        createPortal(
          <div
            className="documents-modal-overlay ts-tl-detail-overlay ts-appr-detail-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ts-appr-detail-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeDetail();
            }}
          >
            <div className="documents-modal ts-tl-detail-modal ts-appr-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ts-tl-detail-modal-header">
                <div>
                  <p className="ts-tl-detail-eyebrow">{t("app.timesheets.approvals.detailModalEyebrow")}</p>
                  <h2 className="ts-tl-detail-title" id="ts-appr-detail-title">
                    {t("app.timesheets.approvals.detailModalTitle", {
                      period: detailDto?.periodLabel || detailRow.periodLabel,
                    })}
                  </h2>
                  <p className="ts-tl-detail-sub">{t("app.timesheets.approvals.detailModalSubtitle")}</p>
                </div>
                <button type="button" className="btn btn-light documents-btn" onClick={closeDetail}>
                  {t("app.timesheets.common.close")}
                </button>
              </div>
              <div className="ts-appr-detail-hero px-3 px-md-4 pt-3 border-bottom">
                <div className="row g-3 align-items-center">
                  <div className="col-12 col-md-8">
                    <TimeSheetPersonCell
                      variant="approval"
                      name={detailRow.employeeName}
                      designation={detailRow.designation}
                      email={detailRow.email}
                      photoUrl={detailRow.photoUrl}
                    />
                  </div>
                  <div className="col-12 col-md-4 text-md-end">
                    <div className="ts-appr-detail-meta">
                      <span className="ts-appr-detail-meta__hours">
                        {detailDto != null ? `${Number(detailDto.totalHours).toFixed(1)}h` : "—"}
                      </span>
                      <span className="ts-appr-detail-meta__label">{t("app.timesheets.approvals.detailTotalWeek")}</span>
                    </div>
                    <div className="mt-2">
                      <TimeSheetStatusBadge
                        status={getApprovalQueueRowBadgeStatus({
                          approvalStatus: detailDto?.approvalStatus ?? detailRow.approvalStatus,
                        })}
                        label={getApprovalQueueRowStatusLabel(
                          {
                            approvalStatus: detailDto?.approvalStatus ?? detailRow.approvalStatus,
                          },
                          t
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-tl-detail-body">
                {detailLoading ? (
                  <div className="d-flex justify-content-center align-items-center gap-2 py-5 text-muted" role="status">
                    <span className="spinner-border spinner-border-sm text-primary" aria-hidden />
                    {t("app.timesheets.approvals.detailLoading")}
                  </div>
                ) : (
                  (detailDto?.detailDays || []).map((day) => (
                    <div key={day.dayLabel} className="ts-tl-detail-day-block">
                      <h3 className="ts-tl-detail-day-title">{day.dayLabel}</h3>
                      <table className="ts-tl-detail-table">
                        <thead>
                          <tr>
                            <th>{t("app.timesheets.timeLog.detailColProject")}</th>
                            <th className="text-end">{t("app.timesheets.timeLog.detailColHours")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {day.entries.map((e, idx) => (
                            <tr key={`${day.dayLabel}-${idx}-${e.projectName}`}>
                              <td>{e.projectName}</td>
                              <td className="text-end fw-semibold">{Number(e.hours).toFixed(1)}h</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
                {!detailLoading && detailDto && (!detailDto.detailDays || detailDto.detailDays.length === 0) ? (
                  <p className="text-muted px-3 py-4 mb-0">{t("app.timesheets.approvals.detailNoEntries")}</p>
                ) : null}
              </div>
              <div className="ts-tl-detail-footer d-flex flex-wrap gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary documents-btn"
                  disabled={!detailRow.timeSheetId || detailPdfLoading}
                  onClick={() => downloadDetailPdf()}
                >
                  {detailPdfLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                      {t("app.timesheets.approvals.pdfDownloading")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-pdf me-2" aria-hidden />
                      {t("app.timesheets.approvals.downloadPdf")}
                    </>
                  )}
                </button>
                <button type="button" className="btn btn-primary documents-btn ts-ts-primary-gradient" onClick={closeDetail}>
                  {t("app.timesheets.common.close")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {modal &&
        createPortal(
          <div
            className="documents-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ts-action-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) setModal(null);
            }}
          >
            <div
              className="documents-modal documents-modal--upload-single"
              style={{ maxWidth: 480 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
                <div className="documents-upload-modal-header">
                  <div>
                    <p className="documents-upload-modal-eyebrow">{t("app.timesheets.approvals.title")}</p>
                    <h2 className="documents-upload-modal-title" id="ts-action-title">
                      {modal.action === ACTION.APPROVE && t("app.timesheets.approvals.modalApproveTitle")}
                      {modal.action === ACTION.REJECT && t("app.timesheets.approvals.modalRejectTitle")}
                      {modal.action === ACTION.RETURN && t("app.timesheets.approvals.modalReturnTitle")}
                    </h2>
                    <p className="documents-page-description" style={{ marginTop: 8 }}>
                      {modal.row.employeeName} — {modal.row.periodLabel}
                    </p>
                  </div>
                  <button type="button" className="btn btn-light documents-btn" onClick={() => setModal(null)}>
                    {t("app.timesheets.common.close")}
                  </button>
                </div>
                <div className="documents-modal-sheet documents-modal-sheet--upload ts-reason-modal-body">
                  {(modal.action === ACTION.REJECT || modal.action === ACTION.RETURN) && (
                    <>
                      <label htmlFor="ts-reason" className="documents-form-label">
                        {t("app.timesheets.approvals.reasonLabel")}
                      </label>
                      <textarea id="ts-reason" placeholder={t("app.timesheets.approvals.reasonPlaceholder")} />
                    </>
                  )}
                  <div className="documents-modal-actions" style={{ marginTop: "1rem" }}>
                    <button type="button" className="btn btn-light documents-btn" onClick={() => setModal(null)}>
                      {t("app.timesheets.common.cancel")}
                    </button>
                    <button
                      type="button"
                      className={`btn documents-btn ${modal.action === ACTION.APPROVE ? "btn-success" : "btn-primary"}`}
                      style={
                        modal.action === ACTION.REJECT
                          ? { background: "#b91c1c", color: "#fff", border: "none" }
                          : modal.action === ACTION.RETURN
                            ? { background: "#b45309", color: "#fff", border: "none" }
                            : undefined
                      }
                      disabled={decisionLoading}
                      onClick={() => {
                        const ta = document.getElementById("ts-reason");
                        const reason = ta ? ta.value.trim() : "";
                        if ((modal.action === ACTION.REJECT || modal.action === ACTION.RETURN) && !reason) {
                          Common.showErrorMessage(t("app.timesheets.approvals.reasonRequired"));
                          return;
                        }
                        applyAction(reason);
                      }}
                    >
                      {modal.action === ACTION.APPROVE && t("app.timesheets.approvals.confirmApprove")}
                      {modal.action === ACTION.REJECT && t("app.timesheets.approvals.confirmReject")}
                      {modal.action === ACTION.RETURN && t("app.timesheets.approvals.confirmReturn")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TimeSheetApprovalsPage;
